import Head from 'next/head';
import type { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from './_app';
import React from 'react';
import Layout from '../layouts';
import { AppSettings } from '@/shared/app-common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { pricingJsonLd, pricingStructure } from '@/lib/jsonld';
import { orderSubsQueryRequest } from '@/lib/service/home';
const PlanContent = dynamic(() => import('@/components/Plan'), {
  ssr: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, req, resolvedUrl } = context;

  const [localeRes, tdkRes] = await Promise.all([
    serverSideTranslations(locale, ['common']),
    getTdkApi(resolvedUrl.split('?')[0], locale),
  ]);
  const { data: planList } = await orderSubsQueryRequest('main', locale);

  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const planItems: pricingStructure[] = planList.map((plan: any) => {
    return {
      name: plan.name,
      price: plan.amount,
      currency: 'USD',
    };
  });
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;
  const jsonLd = pricingJsonLd(
    {
      title: tdkRes?.data?.title ?? tempData['head_Plan_Meta_Title'],
      desc: tdkRes?.data?.description ?? tempData['head_Plan_Meta_Desc'],
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    { url: siteUrl },
    planItems
  );
  return {
    props: {
      ...localeRes,
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      locale,
      title: tdkRes?.data?.title ?? tempData['head_Plan_Meta_Title'],
      description: tdkRes?.data?.description ?? tempData['head_Plan_Meta_Desc'],
      jsonLd,
    },
  };
};

const Index: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  return <PlanContent data={props} />;
};

Index.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const description = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={description} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="canonical" href={page.props?.herf} />
          <link rel="icon" href={AppSettings.icoPath} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
    >
      {page}
    </Layout>
  );
};

export default Index;
